import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight 
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  AreaChart,
  Area,
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Analytics() {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [ltvSegments, setLtvSegments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAnalytics();
    }
  }, [user]);

  const fetchAnalytics = async () => {
    try {
      const [dashRes, revRes, ltvRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, { withCredentials: true }),
        axios.get(`${API}/analytics/revenue-chart`, { withCredentials: true }),
        axios.get(`${API}/analytics/ltv-segments`, { withCredentials: true })
      ]);
      
      setDashboardData(dashRes.data);
      setRevenueData(revRes.data.data || []);
      setLtvSegments(ltvRes.data.segments || []);
    } catch (e) {
      console.error('Failed to fetch analytics:', e);
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <AlertTriangle className="w-16 h-16 mx-auto text-[#FF0055] mb-4" />
            <h2 className="font-outfit font-bold text-2xl text-white mb-2">Access Denied</h2>
            <p className="text-[#A1A1AA]">You need admin privileges to view analytics.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const statCards = dashboardData ? [
    { 
      icon: Users, 
      label: 'Total Users', 
      value: dashboardData.total_users,
      change: '+12%',
      positive: true,
      color: '#00F0FF' 
    },
    { 
      icon: Users, 
      label: 'Active Users', 
      value: dashboardData.active_users,
      change: '+8%',
      positive: true,
      color: '#7000FF' 
    },
    { 
      icon: DollarSign, 
      label: 'Total Revenue', 
      value: `$${dashboardData.total_revenue.toLocaleString()}`,
      change: '+23%',
      positive: true,
      color: '#00F0FF' 
    },
    { 
      icon: AlertTriangle, 
      label: 'Churn Risk', 
      value: `${dashboardData.churn_risk}%`,
      change: '-5%',
      positive: true,
      color: '#FF0055' 
    },
  ] : [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1A1A1E] border border-[#27272A] rounded-md p-3">
          <p className="text-white font-medium">{label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl text-white mb-2">
            Analytics Dashboard
          </h1>
          <p className="text-[#A1A1AA]">
            Sales intelligence, LTV predictions, and user engagement metrics
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, idx) => (
              <div key={idx} className="h-32 rounded-md bg-[#121214] animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {statCards.map((stat, idx) => (
                <div 
                  key={idx}
                  className="p-4 rounded-md bg-[#121214] border border-[#27272A] animate-fade-in"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div 
                      className="w-10 h-10 rounded-md flex items-center justify-center"
                      style={{ backgroundColor: `${stat.color}15`, border: `1px solid ${stat.color}30` }}
                    >
                      <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${stat.positive ? 'text-green-400' : 'text-[#FF0055]'}`}>
                      {stat.positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {stat.change}
                    </div>
                  </div>
                  <p className="text-2xl font-outfit font-bold text-white">{stat.value}</p>
                  <p className="text-sm text-[#A1A1AA]">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Revenue Chart */}
              <div className="lg:col-span-2 p-6 rounded-md bg-[#121214] border border-[#27272A] animate-fade-in stagger-2">
                <h3 className="font-outfit font-bold text-lg text-white mb-4">Revenue & User Growth</h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenueData}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#00F0FF" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#00F0FF" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#7000FF" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#7000FF" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                      <XAxis dataKey="month" stroke="#A1A1AA" fontSize={12} />
                      <YAxis stroke="#A1A1AA" fontSize={12} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#00F0FF" 
                        fillOpacity={1} 
                        fill="url(#colorRevenue)" 
                        name="Revenue ($)"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="users" 
                        stroke="#7000FF" 
                        fillOpacity={1} 
                        fill="url(#colorUsers)" 
                        name="Users"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* LTV Segments */}
              <div className="p-6 rounded-md bg-[#121214] border border-[#27272A] animate-fade-in stagger-3">
                <h3 className="font-outfit font-bold text-lg text-white mb-4">LTV Segments</h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={ltvSegments}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {ltvSegments.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Upsell Opportunities */}
            {dashboardData?.upsell_opportunities?.length > 0 && (
              <div className="p-6 rounded-md bg-[#121214] border border-[#27272A] animate-fade-in stagger-4">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-[#00F0FF]" />
                  <h3 className="font-outfit font-bold text-lg text-white">Upsell Opportunities</h3>
                </div>
                <p className="text-sm text-[#A1A1AA] mb-4">
                  High-engagement users ready for premium features
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#27272A]">
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#A1A1AA]">User</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#A1A1AA]">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#A1A1AA]">Engagement Score</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#A1A1AA]">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.upsell_opportunities.map((user, idx) => (
                        <tr key={idx} className="border-b border-[#27272A]/50 hover:bg-[#1A1A1E]">
                          <td className="py-3 px-4 text-white">{user.name || 'User'}</td>
                          <td className="py-3 px-4 text-[#A1A1AA]">{user.email}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-[#27272A] rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-[#00F0FF] rounded-full"
                                  style={{ width: `${user.engagement_score}%` }}
                                />
                              </div>
                              <span className="text-sm text-[#00F0FF]">{user.engagement_score}%</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <button className="text-sm text-[#00F0FF] hover:underline">
                              Send Offer
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
